import { useCallback, useEffect, useState } from 'react';
import { isBelowMinimum, limitToCents } from '../../lib/amount';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import authService from '../../services/auth.service';
import agentService from '../../services/agent.service';
import depositsService from '../../services/deposits.service';
import managerService, { type ClientSummary } from '../../services/manager.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import { colors, gradients } from '../../styles/theme';
import type { DepositRequest, DepositStatus } from '../../types/finance';

const STATUS_TONE: Record<DepositStatus, BadgeTone> = {
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
  reversed: 'neutral',
};

export default function AgentDeposits(): JSX.Element {
  const [deposits, setDeposits] = useState<DepositRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPropose, setShowPropose] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await depositsService.list({ size: 50 });
      setDeposits(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load your deposits.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <>
      <PageHeader
        title="Deposits"
        subtitle="Propose a client deposit. A manager reviews and approves before the funds post to the ledger."
        actions={
          <button
            type="button"
            onClick={() => setShowPropose(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: gradients.gold, color: colors.navy }}
          >
            Propose deposit
          </button>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
            color: colors.danger,
          }}
        >
          {error}
        </div>
      )}

      <DepositTable deposits={deposits} loading={loading} />

      {showPropose && (
        <ProposeDepositModal
          onClose={() => setShowPropose(false)}
          onSubmitted={(msg) => {
            setShowPropose(false);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 5000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface TableProps {
  readonly deposits: DepositRequest[];
  readonly loading: boolean;
}

function DepositTable({ deposits, loading }: TableProps): JSX.Element {
  if (loading) {
    return <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  }
  if (deposits.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        You have not proposed any deposits yet.
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <table className="w-full text-sm">
        <thead style={{ background: colors.canvas, color: colors.muted }}>
          <tr>
            <th className="text-left font-medium px-4 py-3">Member</th>
            <th className="text-left font-medium px-4 py-3">Product</th>
            <th className="text-right font-medium px-4 py-3">Amount</th>
            <th className="text-left font-medium px-4 py-3">Reference</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-left font-medium px-4 py-3">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {deposits.map((d, idx) => (
            <tr
              key={d.id}
              style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
            >
              <td className="px-4 py-3">
                <Link
                  to={`/agent/clients/${d.user_id}`}
                  state={{ clientName: fullName({ first_name: d.user_first_name, last_name: d.user_last_name }), clientEmail: d.user_email }}
                  className="font-medium hover:underline"
                  style={{ color: colors.ink }}
                >
                  {fullName({ first_name: d.user_first_name, last_name: d.user_last_name })}
                </Link>
                <div className="text-xs" style={{ color: colors.mutedSoft }}>{d.user_email}</div>
              </td>
              <td className="px-4 py-3" style={{ color: colors.inkSoft }}>{d.product_name}</td>
              <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                {formatMoney(d.amount, d.currency_code)}
              </td>
              <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                {d.reference_number ?? '—'}
              </td>
              <td className="px-4 py-3">
                <StatusBadge tone={STATUS_TONE[d.status]}>{d.status}</StatusBadge>
              </td>
              <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                {formatDateTime(d.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ProposeProps {
  readonly onClose: () => void;
  readonly onSubmitted: (flashMessage: string) => void;
}

function ProposeDepositModal({ onClose, onSubmitted }: ProposeProps): JSX.Element {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [eligibleIds, setEligibleIds] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const role = authService.getRole();
        const clientsP = role === 'agent'
          ? agentService.listMyClients()
          : managerService.listClients();
        const [c, p] = await Promise.all([
          clientsP,
          portfolioService.listProducts(),
        ]);
        if (!cancelled) {
          setClients(c);
          setProducts(p);
        }
      } catch (e: unknown) {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load form options.'));
      }
    })();
    return () => { cancelled = true; };
  }, [loadKey]);

  // When a client is selected, restrict the product list to the products that
  // client holds an ACTIVE signed contract for — a deposit funds a CONTRACTED
  // product, not an arbitrary one. `null` = no client picked / still loading.
  useEffect(() => {
    if (!userId) { setEligibleIds(null); return; }
    let cancelled = false;
    setEligibleIds(null);
    depositsService
      .listClientProducts(userId)
      .then((ids) => {
        if (cancelled) return;
        const set = new Set(ids);
        setEligibleIds(set);
        // Drop a previously-picked product if it isn't eligible for this client.
        setProductId((cur) => (cur && set.has(cur) ? cur : ''));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(getApiErrorMessage(e, "Could not load the client's products."));
      });
    return () => { cancelled = true; };
  }, [userId]);

  // Products the SELECTED client can be funded into (their contracted
  // products), excluding any whose currency funds could not answer (0073) —
  // a deposit can't be proposed without knowing what currency it is in.
  const visibleProducts = (products ?? []).filter(
    (p) => (eligibleIds?.has(p.id) ?? false) && p.currency_code !== null,
  );

  const product = products?.find((p) => p.id === productId);
  const validAmount = (() => {
    const n = Number(amount);
    return !Number.isNaN(n) && n > 0;
  })();
  // The minimum binds a client's FIRST contribution only; the server knows whether this one is
  // (it reads the ledger), so the form warns and lets the server decide.
  const belowMinimum = isBelowMinimum(amount, product?.min_investment);
  const formValid = userId && productId && validAmount && !!product?.currency_code;

  const submit = async (): Promise<void> => {
    if (!formValid || !product?.currency_code) return;
    setBusy(true);
    setError(null);
    try {
      await depositsService.propose({
        user_id: userId,
        product_id: productId,
        amount,
        currency_code: product.currency_code,
        notes: notes.trim() || null,
      });
      onSubmitted(`Deposit proposed — awaiting manager review.`);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not submit the proposal.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Propose deposit
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Record a client transfer that arrived via SPEI / wire. A manager reviews
        before it posts to the ledger — never approve your own proposals.
      </p>

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm flex items-start justify-between gap-3"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          <span>{error}</span>
          {clients === null && (
            <button
              type="button"
              onClick={() => setLoadKey((k) => k + 1)}
              className="shrink-0 text-xs font-semibold underline"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        <Field label="Member">
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={!clients || busy}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="">— Select client —</option>
            {[...(clients ?? [])]
              .sort((a, b) => fullName(a).localeCompare(fullName(b)))
              .map((c) => (
                <option key={c.id} value={c.id}>{fullName(c)} · {c.email}</option>
              ))}
          </select>
        </Field>

        <Field label="Product">
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            disabled={!userId || eligibleIds === null || busy}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="">
              {userId ? '— Select product —' : '— Select a client first —'}
            </option>
            {[...visibleProducts]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.currency_code})</option>
              ))}
          </select>
          {userId && eligibleIds !== null && visibleProducts.length === 0 && (
            <p className="text-xs mt-1" style={{ color: colors.warning }}>
              This client has no contracted products to fund — they must sign a product
              contract before a deposit can be proposed.
            </p>
          )}
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={product ? `Amount (${product.currency_code})` : 'Amount'}>
            <input
              type="number"
              step="0.01"
              inputMode="decimal"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(limitToCents(e.target.value))}
              disabled={busy}
              placeholder="50000.00"
              className="w-full px-3 py-2 rounded-lg text-sm font-mono"
              style={{ border: `1px solid ${colors.border}` }}
            />
            {product?.currency_code && Number(product.min_investment ?? 0) > 0 && (
              <p
                className="text-xs mt-1"
                style={{ color: belowMinimum ? colors.warning : colors.muted }}
              >
                {`Minimum first contribution: ${formatMoney(product.min_investment, product.currency_code)}`}
                {belowMinimum && (
                  <span className="block">Below it, only a client who already holds this series can add it.</span>
                )}
              </p>
            )}
          </Field>
        </div>

        <Field label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            rows={3}
            maxLength={2000}
            placeholder="Free-form context for the reviewer."
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !formValid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !formValid ? colors.disabledBg : gradients.gold,
            color: busy || !formValid ? colors.disabledText : colors.navy,
          }}
        >
          {busy ? 'Submitting…' : 'Submit proposal'}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {label}
      </label>
      {children}
    </div>
  );
}
